import escape from 'lodash/escape'

import Button from 'common/components/button'
import { useAppSelector } from 'common/hooks/redux'
import { replaceNewLineEmojiAsString } from 'common/variantsUtils'

import { Fragment } from '../interfaces'

type Props = {
  controlText: string
  description: string | undefined
  fragments: Fragment[]
  isAdvancedSettingsShown: boolean
  onAdvancedSettingsClick: () => void
}

const colors = ['bg-orange-200', 'bg-amber-200', 'bg-green-200', 'bg-blue-200']

const ControlVariant = ({
  controlText,
  description,
  fragments,
  isAdvancedSettingsShown,
  onAdvancedSettingsClick,
}: Props) => {
  const descriptionFragments = useAppSelector(
    (state) => state.contentGeneration.descriptionFragments
  )

  function enhanceText(controlText: string, fragments: Fragment[]): string {
    // Prevent angular brackets from being interpreted as HTML tags
    let enchancedText = escape(controlText)

    if (fragments.length > 0) {
      fragments.forEach(({ name }, index) => {
        if (name) {
          enchancedText = enchancedText
            .replace(
              new RegExp(name, 'g'),
              `<span class="rounded py-2 px-1 ${
                colors[index % colors.length]
              }">${name}</span>`
            )
            .replace(/〽/g, '\n')
            .trim()
            .replace(/\n/g, '<br/>')
        }
      })
    } else {
      enchancedText = replaceNewLineEmojiAsString(enchancedText)
        .trim()
        .replace(/\n/g, '<br/>')
    }

    return enchancedText
  }

  const enchancedControlText = enhanceText(controlText, fragments)
  const enchancedDescriptionText = enhanceText(
    description || '',
    descriptionFragments
  )

  return (
    <div className="p-6 font-medium text-lg text-gray-800 border-b border-dashed border-coolGray-300 bg-coolGray-50 bg-opacity-25">
      <div className="flex mb-9 items-baseline justify-between">
        {description ? (
          <div>Your control variant and content description</div>
        ) : (
          <div>
            Your control variant{' '}
            <span className="ml-4 font-normal text-sm text-gray-500">
              You configured this on step 1.
            </span>
          </div>
        )}
        {isAdvancedSettingsShown && (
          <Button
            variant="primary"
            onClick={onAdvancedSettingsClick}
            data-cy="advanced-settings-button"
          >
            Advanced settings
          </Button>
        )}
      </div>
      <div
        className="font-normal"
        dangerouslySetInnerHTML={{ __html: enchancedControlText }}
      />
      {description && (
        <div
          className="pt-6 mt-6 font-normal border-t border-dashed border-coolGray-300"
          dangerouslySetInnerHTML={{ __html: enchancedDescriptionText }}
        />
      )}
    </div>
  )
}

export default ControlVariant
